import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Sparkles, Loader2 } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import TarotCard from '../components/TarotCard';
import SiteFooter from '../components/SiteFooter';
import { format } from 'date-fns';

const SharedReading = () => {
  const navigate = useNavigate();
  const { shareId } = useParams();
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchShared();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId]);

  const fetchShared = async () => {
    try {
      const response = await axios.get(`${API}/share/${shareId}`);
      setReading(response.data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const getSpreadLabel = (type) => ({
    'single': 'Single Card',
    'three-card': 'Three Card',
    'five-card': 'Five Card',
    'celtic-cross': 'Celtic Cross',
    'daily': 'Daily Reading',
  }[type] || type);

  const getPositionLabel = (index, spreadType) => {
    if (spreadType === 'three-card') return ['Past', 'Present', 'Future'][index];
    if (spreadType === 'five-card') return ['Core Issue', 'Past Influence', 'Emerging Future', 'External Influence', 'Best Path'][index];
    if (spreadType === 'single' || spreadType === 'daily') return 'Insight';
    if (spreadType === 'celtic-cross') return `Position ${index + 1}`;
    return '';
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F8F9FA]">
      <nav className="glass-effect border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-2xl font-heading font-light">Seatask Arcane AI</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button
              onClick={() => navigate('/')}
              data-testid="get-your-reading-button"
              className="px-4 py-2 bg-[#D4AF37] text-[#050505] rounded-md hover:bg-[#F3E5AB] transition-all text-sm font-medium"
            >
              Get Your Reading
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="shared-reading-page">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
          </div>
        ) : error || !reading ? (
          <div className="glass-effect rounded-lg p-12 text-center">
            <h2 className="text-2xl font-heading font-medium mb-3">Reading Not Found</h2>
            <p className="text-zinc-400 mb-6">This shared reading does not exist or has been removed.</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 border border-[#D4AF37] text-[#D4AF37] rounded-md hover:bg-[#D4AF37] hover:text-[#050505] transition-all"
            >
              Get Your Own Reading
            </button>
          </div>
        ) : (
          <div>
            <div className="text-center mb-12">
              <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
                Shared Reading · {format(new Date(reading.created_at), 'MMMM d, yyyy')}
              </p>
              <h1 className="text-5xl font-heading font-light tracking-tighter mb-4">
                {getSpreadLabel(reading.spread_type)}
              </h1>
              {reading.question && (
                <p className="text-[#D4AF37] text-lg italic">"{reading.question}"</p>
              )}
            </div>

            <div className="flex flex-wrap justify-center gap-6 mb-12">
              {reading.cards.map((card, index) => (
                <div key={index} className="w-52 sm:w-56 lg:w-64 text-center">
                  <TarotCard card={card} isRevealed={true} />
                  <div className="mt-3">
                    <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-1">
                      {getPositionLabel(index, reading.spread_type)}
                    </p>
                    <p className="text-sm font-heading">{card.name}</p>
                    {card.is_reversed && <p className="text-xs text-[#D4AF37]">Reversed</p>}
                  </div>
                </div>
              ))}
            </div>

            <div className="glass-effect rounded-lg p-8">
              <h2 className="text-2xl font-heading font-medium mb-4">Interpretation</h2>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{reading.interpretation}</p>
            </div>

            <div className="text-center mt-12">
              <button
                onClick={() => navigate('/')}
                data-testid="cta-get-reading"
                className="px-8 py-4 bg-[#D4AF37] text-[#050505] rounded-md hover:bg-[#F3E5AB] transition-all font-medium"
              >
                Get Your Own Reading
              </button>
            </div>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
};

export default SharedReading;
